import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { reviewsApi } from "../../lib/reviews";
import { teenDashboardApi } from "../../lib/teenDashboard";
import type { CancelledGigHistoryItemResponse } from "../../types/cancelledGigTypes";
import type { ResolvedGigIssueItemResponse } from "../../types/resolvedIssueTypes";
import type { TeenDashboardCompletedGigItemResponse } from "../../types/teenDashboardTypes";
import type { CreateReviewRequest, ReviewCandidateResponse, ReviewResponse } from "../../types/reviewTypes";
import { apiErrorToMessage } from "../../utils/ApiUtils";
import { formatUsdFromCents } from "../../utils/CurrencyUtils";
import { formatTeenGigCompensation } from "../../utils/GigCompensationUtils";
import CompletedGigsReviewsWorkspace, {
  type CombinedCompletedGigItem,
  type CombinedCancelledGigItem,
  type CombinedResolvedIssueItem,
} from "./CompletedGigsReviewsWorkspace";

const TeenReviewsWorkspace = () => {
  const { user } = useAuth();
  const [receivedReviews, setReceivedReviews] = useState<ReviewResponse[]>([]);
  const [submittedReviews, setSubmittedReviews] = useState<ReviewResponse[]>([]);
  const [completedGigs, setCompletedGigs] = useState<TeenDashboardCompletedGigItemResponse[]>([]);
  const [resolvedIssues, setResolvedIssues] = useState<ResolvedGigIssueItemResponse[]>([]);
  const [cancelledGigs, setCancelledGigs] = useState<CancelledGigHistoryItemResponse[]>([]);
  const [reviewCandidates, setReviewCandidates] = useState<ReviewCandidateResponse[]>([]);
  const [totalEarnedCents, setTotalEarnedCents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    let mounted = true;

    const loadWorkspace = async () => {
      try {
        setLoading(true);
        setErrorMessage(null);

        const [received, submitted, completed, resolved, cancelled, candidates, snapshot] = await Promise.all([
          reviewsApi.listUserReviews(user.id, "WORKER_PERFORMANCE"),
          reviewsApi.listMySubmitted(),
          teenDashboardApi.listCompletedGigs(),
          teenDashboardApi.listResolvedIssues(),
          teenDashboardApi.listCancelledGigs(),
          reviewsApi.listMyCandidates(),
          teenDashboardApi.getSnapshot(),
        ]);

        if (!mounted) {
          return;
        }

        setReceivedReviews(received);
        setSubmittedReviews(submitted.filter((review) => review.reviewContext === "POSTER_EXPERIENCE"));
        setCompletedGigs(completed);
        setResolvedIssues(resolved);
        setCancelledGigs(cancelled);
        setReviewCandidates(candidates.filter((candidate) => candidate.requiredContext === "POSTER_EXPERIENCE"));
        setTotalEarnedCents(snapshot.totalEarnedCents);
      } catch (error) {
        if (!mounted) {
          return;
        }

        setErrorMessage(apiErrorToMessage(error, "We couldn't load the completed gigs and reviews workspace."));
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void loadWorkspace();

    return () => {
      mounted = false;
    };
  }, [user?.id]);

  const normalizedCompletedGigs = useMemo<CombinedCompletedGigItem[]>(() => {
    const candidateByRequestId = new Map(reviewCandidates.map((candidate) => [candidate.gigRequestId, candidate]));
    const submittedReviewByRequestId = new Map(submittedReviews.map((review) => [review.gigRequestId, review]));

    return completedGigs.map((gig) => {
      const candidate = candidateByRequestId.get(gig.gigRequestId) ?? null;
      const submittedReview = submittedReviewByRequestId.get(gig.gigRequestId) ?? null;
      const counterpartName =
        candidate
          ? [candidate.revieweeFirstName, candidate.revieweeLastName].filter(Boolean).join(" ")
          : [submittedReview?.revieweeFirstName, submittedReview?.revieweeLastName].filter(Boolean).join(" ");

      return {
        requestId: gig.gigRequestId,
        gigId: gig.gigId,
        gigTitle: gig.gigTitle,
        paymentAmountCents: gig.paymentAmountCents,
        volunteer: gig.volunteer,
        teensRequired: gig.teensRequired,
        coveredSeats: gig.coveredSeats,
        completedAt: gig.completedAt,
        counterpartName: counterpartName || null,
        submittedReview,
        reviewStatus: candidate ? "needs_review" : "reviewed",
      };
    });
  }, [completedGigs, reviewCandidates, submittedReviews]);

  const normalizedResolvedIssues = useMemo<CombinedResolvedIssueItem[]>(
    () =>
      resolvedIssues.map((issue) => ({
        issueId: issue.issueId,
        gigId: issue.gigId,
        gigTitle: issue.gigTitle,
        counterpartName: [issue.counterpartFirstName, issue.counterpartLastName].filter(Boolean).join(" ") || null,
        reportedBySelf: issue.reportedByRole === "TEEN",
        issueScope: issue.issueScope,
        coveredSeats: issue.coveredSeats,
        status: issue.status,
        resolvedAt: issue.resolvedAt,
        resolutionReason: issue.resolutionReason,
      })),
    [resolvedIssues],
  );

  const normalizedCancelledGigs = useMemo<CombinedCancelledGigItem[]>(
    () =>
      cancelledGigs.map((gig) => ({
        requestId: gig.gigRequestId,
        gigId: gig.gigId,
        gigTitle: gig.gigTitle,
        paymentAmountCents: gig.paymentAmountCents,
        volunteer: gig.volunteer,
        teensRequired: gig.teensRequired,
        coveredSeats: gig.coveredSeats,
        paymentStatus: gig.paymentStatus,
        cancelledAt: gig.cancelledAt,
        refundedAt: gig.refundedAt,
        counterpartName: [gig.counterpartFirstName, gig.counterpartLastName].filter(Boolean).join(" ") || null,
        cancelledBySelf: gig.cancelledByRole === "TEEN",
      })),
    [cancelledGigs],
  );

  const handleSubmitReview = async (gig: CombinedCompletedGigItem, payload: CreateReviewRequest) => {
    const createdReview = await reviewsApi.create(gig.requestId, {
      ...payload,
      reviewContext: "POSTER_EXPERIENCE",
    });

    setSubmittedReviews((current) => [createdReview, ...current]);
    setReviewCandidates((current) => current.filter((candidate) => candidate.gigRequestId !== gig.requestId));
  };

  return (
    <CompletedGigsReviewsWorkspace
      copy={{
        eyebrow: "Reviews",
        title: "Completed Gigs, Reviews, and Issues",
        intro:
          "Track the feedback you've earned on completed gigs and finish any poster reviews you still owe without leaving the page.",
        receivedLabel: "Received reviews",
        completedLabel: "Completed gigs",
        needsReviewLabel: "Needs your review",
        resolvedLabel: "Resolved issues",
        cancelledLabel: "Cancelled gigs",
        completedSummary: "You've reviewed {reviewedCount} completed gigs so far.",
        needsReviewSummary: "Leave poster feedback directly from the completed gigs tab.",
        resolvedSummary: "{resolvedCount} issue decisions are in your work history.",
        cancelledSummary: "{cancelledCount} cancelled gigs are in your history.",
        completedTabLabel: "Completed Gigs",
        receivedTabLabel: "Reviews You Received",
        resolvedTabLabel: "Resolved Issues",
        cancelledTabLabel: "Cancelled Gigs",
        completedTabTitle: "Completed gigs with review status",
        completedTabDescription:
          "See which finished gigs still need your poster feedback and keep your completed work and review history together.",
        receivedTabTitle: "Feedback you received",
        receivedTabDescription:
          "Review how posters rated your work quality, reliability, and follow-through across completed gigs.",
        resolvedTabTitle: "Issues closed after review",
        resolvedTabDescription:
          "Track final support decisions on gigs with payout or refund disputes, including who opened the issue and how it was resolved.",
        cancelledTabTitle: "Cancelled gigs and refunds",
        cancelledTabDescription:
          "Keep a historical record of scheduled gigs that were cancelled, including whether a refund was recorded.",
        receivedReviewTitle: "Feedback on your work",
        missingReviewCallout: "This completed gig is still waiting on your review of the poster.",
        missingReviewDescription:
          "Add your poster-experience review here so the completed gig and your feedback stay in the same workflow.",
        reviewSubmittedLabel: "Your poster review",
        reviewModalTitle: (gig) => `How was your gig with ${gig.counterpartName ?? "this person"}?`,
        reviewModalPlaceholder: "Share how the poster handled communication, clarity, and follow-through.",
        counterpartFallbackLabel: "Gig poster",
      }}
      receivedReviews={receivedReviews}
      completedGigs={normalizedCompletedGigs}
      resolvedIssues={normalizedResolvedIssues}
      cancelledGigs={normalizedCancelledGigs}
      loading={loading}
      errorMessage={errorMessage}
      onSubmitReview={handleSubmitReview}
      compensationFormatter={formatTeenGigCompensation}
      additionalStats={[
        {
          label: "Total earned",
          value: formatUsdFromCents(totalEarnedCents, "$0.00"),
          description: "From paid gigs",
        },
      ]}
    />
  );
};

export default TeenReviewsWorkspace;
